import React from 'react';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaTiktok } from 'react-icons/fa';
import { Globe, Heart, Mail, MapPin, Phone } from 'lucide-react';

const CURRENT_YEAR = new Date().getFullYear();

const QUICK_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/search', label: 'Search Graduates' },
  { to: '/gown-request', label: 'Request Gown' },
  { to: '/booklets', label: 'Graduation Booklets' },
  { to: '/blog', label: 'News & Updates' },
  { to: '/alumni', label: 'Alumni Association' },
];

const CONTACT = [
  { icon: Phone, label: '+254 719 508 039', href: 'tel:+254719508039' },
  { icon: Mail, label: 'info@katinetti.ac.ke', href: 'mailto:info@katinetti.ac.ke' },
  { icon: MapPin, label: 'Tala, Machakos County' },
  { icon: Globe, label: 'www.katinetti.ac.ke', href: 'https://www.katinetti.ac.ke' },
];

const SOCIALS = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/katine.TTI/',
    icon: FaFacebookF,
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@katine.technical?is_from_webapp=1&sender_device=pc',
    icon: FaTiktok,
  },
];

const Footer = () => (
  <footer className="mt-auto bg-brand-950 text-ink-300">
    <div className="container-page py-12">
      <div className="grid grid-cols-2 gap-8 md:grid-cols-4 md:gap-10">
        {/* Brand */}
        <div className="col-span-2 md:col-span-1">
          <div className="mb-4 flex items-center gap-3">
            <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white/10 p-1 ring-1 ring-white/15">
              <img
                src="/images/logo.png"
                alt=""
                className="h-full w-full object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement.textContent = 'K';
                  e.currentTarget.parentElement.className +=
                    ' text-gold-400 font-extrabold text-lg justify-center items-center flex';
                }}
              />
            </span>
            <div>
              <p className="text-base font-bold leading-tight text-white">Katine TTI</p>
              <p className="text-2xs uppercase tracking-wider text-gold-400">
                Technical Training Institute
              </p>
            </div>
          </div>
          <p className="max-w-xs text-sm leading-relaxed text-ink-400">
            Transforming potential into technical expertise. Verify graduation records,
            request gowns, and stay connected with the alumni community.
          </p>
        </div>

        {/* Quick links */}
        <nav aria-label="Footer">
          <h3 className="mb-4 text-2xs font-bold uppercase tracking-widest text-gold-400">
            Quick Links
          </h3>
          <ul className="space-y-2.5">
            {QUICK_LINKS.map(({ to, label }) => (
              <li key={to}>
                <Link
                  to={to}
                  className="text-sm text-ink-400 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Contact */}
        <div>
          <h3 className="mb-4 text-2xs font-bold uppercase tracking-widest text-gold-400">
            Contact
          </h3>
          <ul className="space-y-3">
            {CONTACT.map(({ icon: Icon, label, href }) => (
              <li key={label} className="flex items-start gap-2.5">
                <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold-400" aria-hidden="true" />
                {href ? (
                  <a
                    href={href}
                    target={href.startsWith('http') ? '_blank' : undefined}
                    rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="text-sm text-ink-400 transition-colors hover:text-white"
                  >
                    {label}
                  </a>
                ) : (
                  <span className="text-sm text-ink-400">{label}</span>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Socials */}
        <div className="col-span-2 md:col-span-1">
          <h3 className="mb-4 text-2xs font-bold uppercase tracking-widest text-gold-400">
            Follow Us
          </h3>
          <div className="flex gap-2.5">
            {SOCIALS.map(({ label, href, icon: Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Katine TTI on ${label}`}
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-white/5 text-ink-300 ring-1 ring-white/10 transition-colors hover:bg-gold-400 hover:text-brand-950 hover:ring-gold-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400"
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
              </a>
            ))}
          </div>
          <p className="mt-4 text-xs leading-relaxed text-ink-500">
            Follow for ceremony announcements and alumni news.
          </p>
        </div>
      </div>

      <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-6 text-center md:flex-row md:text-left">
        <p className="text-xs text-ink-500">
          © {CURRENT_YEAR} Katine Technical Training Institute. All rights reserved.
        </p>
        <p className="inline-flex items-center gap-1.5 text-xs text-ink-500">
          Built with
          <Heart className="h-3.5 w-3.5 fill-red-500 text-red-500" aria-hidden="true" />
          <span className="sr-only">love</span>
          for Katine TTI
        </p>
      </div>
    </div>
  </footer>
);

export default Footer;
